<template>
  <CardWithCustomChart class="total-info shadow-low" :chart="chart" v-bind="{ ...titleAndSubtitle }">
    <template #info-top>
      <Grid gaf="column" justify-content="space-between">
        <Grid gap="var(--r-space-sm)">
          <section>
            <div class="text-secondary-5 text-caption-3">Budget</div>
            <div class="text-secondary-1 text-heading-3">
              {{ formatPrice(cardData.budget) }}
              <span class="text-secondary-1 text-caption-3">{{ settings.currency }}</span>
            </div>
          </section>

          <section>
            <div class="text-secondary-5 text-caption-3">Forecasted cost</div>
            <div class="text-secondary-1 text-heading-3">
              {{ formatPrice(cardData.forecasted_cost) }}
              <span class="text-secondary-1 text-caption-3">{{ settings.currency }}</span>
            </div>
          </section>
        </Grid>

        <Grid gap="var(--r-space-sm)">
          <section>
            <div class="text-secondary-5 text-caption-3">Difference</div>
            <div :class="differenceClassColor" class="text-heading-3">
              {{ showPlus ? '+' : '' }}{{ formatPrice(cardData.difference_between_budget_and_forecasted_cost) }}
              <span class="text-secondary-1 text-caption-3">{{ settings.currency }}</span>
            </div>
          </section>

          <section>
            <div class="text-secondary-5 text-caption-3">Difference %</div>
            <div :class="differencePercentageClassColor" class="text-heading-3">
              {{ formatPrice(cardData.difference_as_percentage_from_budget) }}
              <span class="text-secondary-1 text-caption-3">%</span>
            </div>
          </section>
        </Grid>
      </Grid>
    </template>

    <template #info-bottom>
      <Grid align-items="center" gtc="1fr auto" gap="var(--r-space-md)">
        <Grid gtc="12px 1fr" align-items="center">
          <span class="box bg-graph-undecided"></span>
          <div class="text-body-1 text-left">Unknown ({{ cardData.unknown_count }}):</div>
        </Grid>
        <div class="text-body-2">
          {{ formatPrice(cardData.unknown_cost) }} <small>{{ settings.currency }}</small>
        </div>
      </Grid>

      <Grid align-items="center" gtc="1fr auto" gap="var(--r-space-md)">
        <Grid gtc="12px 1fr" align-items="center">
          <span class="box bg-graph-not-likely"></span>
          <div class="text-body-1 text-left">Not likely ({{ cardData.not_likely_count }}):</div>
        </Grid>
        <div class="text-body-2">
          {{ formatPrice(cardData.not_likely_cost) }} <small>{{ settings.currency }}</small>
        </div>
      </Grid>

      <Grid align-items="center" gtc="1fr auto" gap="var(--r-space-md)">
        <Grid gtc="12px 1fr" align-items="center">
          <span class="box bg-graph-likely"></span>
          <div class="text-body-1 text-left">Likely ({{ cardData.likely_count }}):</div>
        </Grid>
        <div class="text-body-2">
          {{ formatPrice(cardData.likely_cost) }} <small>{{ settings.currency }}</small>
        </div>
      </Grid>

      <Grid align-items="center" gtc="1fr auto" gap="var(--r-space-md)">
        <Grid gtc="12px 1fr" align-items="center">
          <span class="box bg-graph-certain"></span>
          <div class="text-body-1 text-left">Certain ({{ cardData.certain_count }}):</div>
        </Grid>
        <div class="text-body-2">
          {{ formatPrice(cardData.certain_cost) }} <small>{{ settings.currency }}</small>
        </div>
      </Grid>

      <Grid align-items="center" gtc="1fr auto" gap="var(--r-space-md)">
        <Grid gtc="12px 1fr" align-items="center">
          <span class="box bg-graph-accepted"></span>
          <div class="text-body-1 text-left">Already accepted ({{ cardData.accepted_count }}):</div>
        </Grid>
        <div class="text-body-2">
          {{ formatPrice(cardData.accepted_cost) }} <small>{{ settings.currency }}</small>
        </div>
      </Grid>

      <div class="text-grey2" style="border-top: 1px solid"></div>

      <Grid align-items="center" gtc="1fr auto" gap="var(--r-space-md)">
        <Grid gtc="12px 1fr" align-items="center">
          <span class="box bg-graph-cancelled"></span>
          <div class="text-body-1 text-left">Already cancelled ({{ cardData.cancelled_count }}):</div>
        </Grid>
        <div class="text-body-2">
          {{ formatPrice(cardData.cancelled_cost) }} <small>{{ settings.currency }}</small>
        </div>
      </Grid>

      <Grid align-items="center" gtc="1fr auto" gap="var(--r-space-md)">
        <Grid gtc="12px 1fr" align-items="center">
          <span class="box bg-graph-for-cancel"></span>
          <div class="text-body-1 text-left">To be cancelled ({{ cardData.to_cancel_count }}):</div>
        </Grid>
        <div class="text-body-2">
          {{ formatPrice(cardData.to_cancel_cost) }} <small>{{ settings.currency }}</small>
        </div>
      </Grid>
    </template>
  </CardWithCustomChart>
</template>

<script>
import * as apiTo from '@/modular-api'
// Libraries
import { mapState } from 'vuex'
// Components
import CardWithCustomChart from '@/modules/financial/shared/card-with-custom-chart/CardWithCustomChart'

export default {
  components: {
    CardWithCustomChart
  },

  data: () => ({
    cardData: null
  }),

  computed: {
    ...mapState('predictions', ['financialPeriodId']),
    ...mapState('recommendations', ['refreshCardsHandler']),

    chart() {
      if (!this.cardData) return null

      const data = this.cardData

      return {
        bars: [
          {
            label: 'last year',
            labelClassColor: 'text-secondary-3',
            labelClassText: 'text-caption-1',
            width: 23,
            data: [{ label: 'Last year', value: Number(data.last_year_cost), class: 'bg-graph-last-year' }]
          },

          {
            label: 'current year',
            labelClassColor: 'text-secondary-3',
            labelClassText: 'text-caption-1',
            width: 58,
            data: [
              { label: 'Unknown', value: Number(data.unknown_cost), class: 'bg-graph-undecided' },
              { label: 'Not likely', value: Number(data.not_likely_cost), class: 'bg-graph-not-likely' },
              { label: 'Likely', value: Number(data.likely_cost), class: 'bg-graph-likely' },
              { label: 'Certain', value: Number(data.certain_cost), class: 'bg-graph-certain' },
              { label: 'Accepted', value: Number(data.accepted_cost), class: 'bg-graph-accepted' },
              { label: 'Cancelled', value: -Number(data.cancelled_cost), class: 'bg-graph-cancelled' },
              { label: 'To cancel', value: -Number(data.to_cancel_cost), class: 'bg-graph-for-cancel' }
            ]
          }
        ],
        dashedLine: {
          value: Number(data.budget),
          bottomLabel: 'Budget'
        },
        barGap: '34px'
      }
    },

    showPlus() {
      const value = this.cardData.difference_between_budget_and_forecasted_cost

      return value && Number(value) > 0
    },

    differenceClassColor() {
      const value = this.cardData.difference_between_budget_and_forecasted_cost

      if (value === null || !Number(value)) return 'text-secondary-1'
      if (Number(value) > 0) return 'text-success-1'
      return 'text-warning-1'
    },

    differencePercentageClassColor() {
      const value = this.cardData.difference_as_percentage_from_budget

      if (value === null || !Number(value)) return 'text-secondary-1'
      if (Number(value) > 0) return 'text-success-1'
      return 'text-warning-1'
    },

    titleAndSubtitle() {
      if (!this.cardData) return null
      return { title: 'Total', subtitle: `${this.cardData.total_count} Total Subscriptions` }
    }
  },

  watch: {
    financialPeriodId: {
      handler: 'getCardData',
      immediate: true
    },

    refreshCardsHandler() {
      this.getCardData(this.financialPeriodId)
    }
  },

  methods: {
    getCardData(id) {
      if (!id) return
      apiTo.GET_RECOMMENDATIONS_TOTAL_CARD_INFO(id).then(({ data }) => (this.cardData = data.result))
    }
  }
}
</script>

<style lang="scss" scoped>
.total-info {
  grid-area: total-info;

  .box {
    width: 8px;
    height: 8px;
  }
}
</style>
